import React, { useState, useEffect } from 'react';
import AddressBookService from '../services/AddressBookService';
import { provider } from './EthersSetup';
import { AddressLink } from './LinkDisplay';

/**
 * AddressDisplay - Displays blockchain addresses with optional nicknames
 * Shows nickname if available, otherwise shows formatted address
 * Provides tooltip with full address and click-to-copy functionality
 * Optionally shows as blockchain explorer link when useExplorerLink is true
 */
const AddressDisplay = ({
  address,
  showFullOnHover = true,
  copyable = true,
  className = '',
  style = {},
  prefix = '',
  suffix = '',
  useExplorerLink = false,
  showBalance = true
}) => {
  const [displayText, setDisplayText] = useState('');
  const [isHovered, setIsHovered] = useState(false);
  const [justCopied, setJustCopied] = useState(false);
  const [balance, setBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);

  useEffect(() => {
    // Update display text when address changes or address book is updated
    const updateDisplayText = () => {
      if (!address) {
        setDisplayText('');
        return;
      }
      setDisplayText(AddressBookService.getNicknameOrAddress(address));
    };

    updateDisplayText();

    // Listen for localStorage changes to update when address book is modified
    const handleStorageChange = (e) => {
      if (e.key === 'gamemaker_address_book') {
        updateDisplayText();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also listen for custom events from the same page
    const handleAddressBookUpdate = () => {
      updateDisplayText();
    };
    window.addEventListener('addressBookUpdated', handleAddressBookUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('addressBookUpdated', handleAddressBookUpdate);
    };
  }, [address]);

  // Fetch balance when address changes or on hover (if showBalance is enabled)
  useEffect(() => {
    const fetchBalance = async () => {
      if (!address || !showBalance || !provider || balanceLoading) return;

      setBalanceLoading(true);
      try {
        const balanceWei = await provider.getBalance(address);
        const balanceEth = parseFloat(balanceWei.toString()) / Math.pow(10, 18);
        setBalance(balanceEth);
      } catch (error) {
        console.warn('Failed to fetch balance for address:', address, error);
        setBalance(null);
      } finally {
        setBalanceLoading(false);
      }
    };

    // Only fetch balance when hovered to avoid unnecessary requests
    if (isHovered && showBalance) {
      fetchBalance();
    }
  }, [address, isHovered, showBalance, balanceLoading]);

  const handleClick = async () => {
    if (!copyable || !address) return;
    
    try {
      await navigator.clipboard.writeText(address);
      setJustCopied(true);
      setTimeout(() => setJustCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy address:', error);
    }
  };

  const getTitle = () => {
    if (!address) return '';

    const addressBook = AddressBookService.getAllEntries();
    const entry = addressBook.find(e =>
      AddressBookService.normalizeAddress(e.address) === AddressBookService.normalizeAddress(address)
    );

    let title = '';
    if (entry && entry.nickname) {
      title = `${entry.nickname}\nAddress: ${address}`;
    } else {
      title = `Address: ${address}`;
    }

    // Add balance information if available
    if (showBalance && balance !== null) {
      const balanceFormatted = balance < 0.001 ? balance.toExponential(3) : balance.toFixed(4);
      title += `\nBalance: ${balanceFormatted} ETH`;
    } else if (showBalance && balanceLoading) {
      title += '\nBalance: Loading...';
    }

    if (copyable) {
      title += '\nClick to copy address';
    }

    return title;
  };

  if (!address) {
    return null;
  }

  // Use AddressLink component when blockchain explorer links are requested
  if (useExplorerLink) {
    return (
      <AddressLink
        value={address}
        provider={provider}
        className={className}
        style={style}
        prefix={prefix}
        suffix={suffix}
        showFullOnHover={showFullOnHover}
        copyable={copyable}
      />
    );
  }

  const baseClassName = `inline-block ${copyable ? 'cursor-pointer hover:bg-gray-100 rounded px-1' : ''} ${className}`;
  const displayStyle = {
    transition: copyable ? 'background-color 0.2s' : 'none',
    ...style
  };

  return (
    <span
      className={baseClassName}
      style={displayStyle}
      onClick={handleClick}
      title={showFullOnHover ? getTitle() : ''}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {prefix}
      <span className={justCopied ? 'text-green-600' : ''}>
        {justCopied ? '✓ Copied!' : displayText}
      </span>
      {suffix}
    </span>
  );
};

/**
 * AddressDisplayLong - Shows nickname + full address format
 * Example: "Alice (0x1234...5678)" or just "0x1234...5678"
 * Optionally shows as blockchain explorer link when useExplorerLink is true
 */
export const AddressDisplayLong = ({
  address,
  showFullOnHover = true,
  copyable = true,
  className = '',
  style = {},
  prefix = '',
  suffix = '',
  useExplorerLink = false,
  showBalance = true
}) => {
  const [displayText, setDisplayText] = useState('');
  const [justCopied, setJustCopied] = useState(false);
  const [balance, setBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const updateDisplayText = () => {
      if (!address) {
        setDisplayText('');
        return;
      }
      setDisplayText(AddressBookService.getDisplayText(address));
    };

    updateDisplayText();

    const handleStorageChange = (e) => {
      if (e.key === 'gamemaker_address_book') {
        updateDisplayText();
      }
    };

    const handleAddressBookUpdate = () => {
      updateDisplayText();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('addressBookUpdated', handleAddressBookUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('addressBookUpdated', handleAddressBookUpdate);
    };
  }, [address]);

  // Fetch balance when address changes or on hover (if showBalance is enabled)
  useEffect(() => {
    const fetchBalance = async () => {
      if (!address || !showBalance || !provider || balanceLoading) return;

      setBalanceLoading(true);
      try {
        const balanceWei = await provider.getBalance(address);
        const balanceEth = parseFloat(balanceWei.toString()) / Math.pow(10, 18);
        setBalance(balanceEth);
      } catch (error) {
        console.warn('Failed to fetch balance for address:', address, error);
        setBalance(null);
      } finally {
        setBalanceLoading(false);
      }
    };

    // Only fetch balance when hovered to avoid unnecessary requests
    if (isHovered && showBalance) {
      fetchBalance();
    }
  }, [address, isHovered, showBalance, balanceLoading]);

  const handleClick = async () => {
    if (!copyable || !address) return;
    
    try {
      await navigator.clipboard.writeText(address);
      setJustCopied(true);
      setTimeout(() => setJustCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy address:', error);
    }
  };

  if (!address) {
    return null;
  }

  // Use AddressLink component when blockchain explorer links are requested
  if (useExplorerLink) {
    return (
      <AddressLink
        value={address}
        provider={provider}
        className={className}
        style={style}
        prefix={prefix}
        suffix={suffix}
        showFullOnHover={showFullOnHover}
        copyable={copyable}
      />
    );
  }

  const getTitleForLong = () => {
    if (!showFullOnHover || !address) return '';

    let title = `Full address: ${address}`;

    // Add balance information if available
    if (showBalance && balance !== null) {
      const balanceFormatted = balance < 0.001 ? balance.toExponential(3) : balance.toFixed(4);
      title += `\nBalance: ${balanceFormatted} ETH`;
    } else if (showBalance && balanceLoading) {
      title += '\nBalance: Loading...';
    }

    if (copyable) {
      title += '\nClick to copy address';
    }

    return title;
  };

  return (
    <span
      className={`inline-block ${copyable ? 'cursor-pointer hover:bg-gray-100 rounded px-1' : ''} ${className}`}
      style={{ transition: copyable ? 'background-color 0.2s' : 'none', ...style }}
      onClick={handleClick}
      title={getTitleForLong()}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {prefix}
      <span className={justCopied ? 'text-green-600' : ''}>
        {justCopied ? '✓ Copied!' : displayText}
      </span>
      {suffix}
    </span>
  );
};

/**
 * Utility function to trigger address book update events
 * Call this after making changes to the address book to update all displays
 */
export const notifyAddressBookUpdated = () => {
  window.dispatchEvent(new Event('addressBookUpdated'));
};

export default AddressDisplay;